import type { DetailedHTMLProps, ReactNode, TableHTMLAttributes } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { FunnelXIcon } from '@phosphor-icons/react';
import type {
  Header,
  Row,
  SortDirection,
  Table as TableType,
} from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';

import { cn } from 'src/utils/classNamesHelper';
import { useKeyboardNavigation } from 'src/utils/useKeyboardNavigation';
import { useKeyPress } from 'src/utils/useKeyPress';

import { ChevronSmallDown, ChevronSmallUp } from '@remarkable/ark-icons';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { TablePagination } from './TablePagination';
import { Tooltip } from './Tooltip';

export const Table = <T,>({
  children,
  className,
  table,
  pagination = false,
  rowWrapper,
  loading = false,
  ...props
}: DetailedHTMLProps<
  TableHTMLAttributes<HTMLTableElement>,
  HTMLTableElement
> & {
  table?: TableType<T>;
  pagination?: boolean;
  loading?: boolean;
  rowWrapper?: (props: { children: ReactNode; row: Row<T> }) => ReactNode;
}) => {
  return (
    <>
      <table className={cn('w-full table-fixed', className)} {...props}>
        {table && (
          <>
            <TableHead table={table} />
            {loading ? (
              <tbody>
                <tr>
                  <td
                    colSpan={table.getVisibleFlatColumns().length}
                    className="h-128 text-neutral-dark-1"
                  >
                    <div className="flex size-full animate-fade-in-up items-center justify-center">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <TableBody table={table} rowWrapper={rowWrapper}>
                {children}
              </TableBody>
            )}
          </>
        )}
        {!table && <>{children}</>}
        {table && pagination && (
          <tfoot>
            <tr>
              <td
                colSpan={table.getVisibleFlatColumns().length}
                className="h-32"
              >
                <TablePagination table={table} />
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </>
  );
};

/** For use with React Table */
export const TableHead = <T,>({ table }: { table: TableType<T> }) => {
  const keyboardEvent = useKeyPress('Tab');
  const [showFocus, setShowFocus] = useState(true);
  const [activeHeader, setActiveHeader] = useState<string | null>(
    table.getAllColumns()[0]?.id ?? null
  );
  const visibleColumns = table.getVisibleFlatColumns();
  const activeColumn = table.getColumn(activeHeader ?? '');

  useEffect(() => {
    if (keyboardEvent && !showFocus) {
      setShowFocus(true);
    }
  }, [keyboardEvent]);

  return (
    <>
      <colgroup>
        {table.getVisibleFlatColumns().map((column) => (
          <col
            key={column.id}
            className={clsx(column.columnDef.meta?.colWidth)}
          />
        ))}
      </colgroup>

      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr
            key={headerGroup.id}
            tabIndex={0}
            className={clsx('group rounded-sm outline-hidden', {
              'ring-pen-blue focus:ring-2': showFocus,
            })}
            data-cy={headerGroup.id}
            onMouseDown={() => setShowFocus(false)}
            onKeyDown={(event) => {
              if (!activeColumn) return;
              if (
                (event.key === 'Enter' || event.key === ' ') &&
                activeColumn.getCanSort()
              ) {
                event.preventDefault();
                activeColumn.toggleSorting();
              }

              if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                activeColumn.getIsSorted() !== 'asc'
                  ? activeColumn.toggleSorting(false)
                  : activeColumn.clearSorting();
              } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                activeColumn.getIsSorted() !== 'desc'
                  ? activeColumn.toggleSorting(true)
                  : activeColumn.clearSorting();
              }

              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                const index = visibleColumns.findIndex(
                  (column) => column.id === activeHeader
                );

                const prevColumn = visibleColumns[index - 1];
                if (prevColumn && prevColumn.getCanSort()) {
                  setActiveHeader(prevColumn.id);
                }
              } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                const index = visibleColumns.findIndex(
                  (column) => column.id === activeHeader
                );
                const nextColumn = visibleColumns[index + 1];
                if (nextColumn && nextColumn.getCanSort()) {
                  setActiveHeader(nextColumn.id);
                }
              }

              setShowFocus(true);
            }}
          >
            {headerGroup.headers.map((header) => (
              <TableHeader
                header={header}
                key={header.id}
                data-cy={header.id}
                onClick={header.column.getToggleSortingHandler()}
                align={header.column.columnDef.meta?.align}
                sorting={
                  header.column.getCanSort()
                    ? header.column.getIsSorted()
                    : undefined
                }
                className={clsx(
                  {
                    'group-focus:bg-pen-blue-light':
                      header.column.id === activeHeader && showFocus,
                  },
                  header.column.columnDef.meta?.colWidth
                )}
                role={header.column.getCanSort() ? 'button' : undefined}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </TableHeader>
            ))}
          </tr>
        ))}
      </thead>
    </>
  );
};

const TableRow = <T,>({
  row,
  rowWrapper,
  getRef,
}: {
  row: Row<T>;
  rowWrapper?: (props: { children: ReactNode; row: Row<T> }) => ReactNode;
  getRef: (el: HTMLTableRowElement | null) => void;
}) => {
  const wrapperProps = rowWrapper ? { row: row } : {};

  const Wrapper = rowWrapper ?? Fragment;

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Wrapper {...(wrapperProps as any)} key={row.id}>
      <tr
        data-cy={row.id}
        key={row.id}
        className="group table-row"
        ref={(el) => getRef(el)}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            data-cy={cell.id}
            truncate={cell.column.columnDef.meta?.truncate}
            align={cell.column.columnDef.meta?.align}
            className={clsx(cell.column.columnDef.meta?.colWidth)}
            fullWidth={cell.column.columnDef.meta?.fullWidth}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </tr>
    </Wrapper>
  );
};

/** For use with React Table */
export const TableBody = <T,>({
  table,
  rowWrapper,
  children,
}: {
  table: TableType<T>;
  rowWrapper?: (props: { children: ReactNode; row: Row<T> }) => ReactNode;
  children?: ReactNode;
}) => {
  const minimumRows = table.getState().pagination.pageSize ?? 0;
  const rowModel = table.getRowModel();
  const remainder = minimumRows - rowModel.rows.length;
  const pages = table.getPageCount();
  const columns = table.getVisibleFlatColumns();
  const shouldFillPage = !!table.options.meta?.fillRowsToMatchPageSize;
  const filters: string[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const globalFilter = table.getState().globalFilter as string | undefined;
    const columnFilters = table
      .getState()
      .columnFilters.map((filter) =>
        Array.isArray(filter.value)
          ? filter.value.join(',')
          : (filter.value as string)
      );

    return [globalFilter, ...columnFilters].filter(Boolean) as string[];
  }, [table.getState().globalFilter, table.getFilteredRowModel().rows.length]);

  const { getRef } = useKeyboardNavigation();

  return (
    <tbody className="animate-fade-in">
      {children ??
        rowModel.rows.map((row, index) => (
          <TableRow
            row={row}
            key={row.id}
            rowWrapper={rowWrapper}
            getRef={(el) => getRef(index, el)}
            aria-label={table.options.meta?.getRowLabel?.(row.original)}
            aria-description={table.options.meta?.getRowDescription?.(
              row.original
            )}
          />
        ))}

      {filters.length > 0 && rowModel.rows.length === 0 && (
        <tr>
          <td colSpan={columns.length} className="h-96 text-neutral-dark-1">
            <div className="flex size-full animate-fade-in-up flex-col items-center justify-center gap-8">
              <span>No results matching &quot;{filters.join(', ')}&quot;</span>

              <Button
                size="small"
                variant="tertiary-neutral"
                onClick={() => {
                  table.resetGlobalFilter(true);
                  table.resetColumnFilters(true);
                }}
              >
                Clear filters
              </Button>
            </div>
          </td>
        </tr>
      )}

      {pages > 1 &&
        remainder > 0 &&
        shouldFillPage &&
        Array.from({ length: remainder }).map((_, index) => (
          <tr key={index} className="h-56">
            {columns.map((column) => (
              <TableCell key={column.id} noPadding>
                &nbsp;
              </TableCell>
            ))}
          </tr>
        ))}
    </tbody>
  );
};

export const TableHeader = <T,>({
  children,
  className,
  sorting,
  header,
  ...props
}: DetailedHTMLProps<
  TableHTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
> & {
  header?: Header<T, unknown>;
  sorting?: SortDirection | false;
}) => {
  return (
    <th
      className={clsx(
        'h-32 border-b border-paper-7 interface-sm',
        {
          'cursor-pointer select-none hover:bg-paper-3':
            typeof sorting !== 'undefined',
        },
        className
      )}
      {...props}
    >
      <div className="flex justify-between gap-8 px-8">
        <div className="flex w-fit items-center gap-8">
          {children}
          {sorting === 'asc' && (
            <ChevronSmallUp size={16} className="text-pen-blue" />
          )}
          {sorting === 'desc' && (
            <ChevronSmallDown size={16} className="text-pen-blue" />
          )}
          {header?.column.getIsFiltered() && (
            <Tooltip
              title={
                <>
                  Clear column filter: &quot;{header.column.getFilterValue()}
                  &quot;
                </>
              }
            >
              <button
                className="rounded-sm p-2 hover:bg-paper-6"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  header.column.setFilterValue(undefined);
                }}
              >
                <FunnelXIcon />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </th>
  );
};

export const TableCell = ({
  children,
  className,
  noPadding = false,
  truncate = true,
  fullWidth = false,
  ...props
}: DetailedHTMLProps<
  TableHTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
> & {
  noPadding?: boolean;
  truncate?: boolean;
  fullWidth?: boolean;
}) => {
  return (
    <td className={clsx('h-56 border-b border-paper-7', className)} {...props}>
      <div
        className={clsx(
          'flex max-w-full items-center body-sm',
          fullWidth ? 'w-full' : 'w-fit',
          {
            'px-8': !noPadding,
          }
        )}
      >
        <div
          className={clsx('relative', fullWidth ? 'w-full' : 'w-fit', {
            truncate: truncate,
          })}
        >
          {children}
        </div>
      </div>
    </td>
  );
};
